<template>
  <div
    class="card mb-2"
    :class="{ 'border-success': pair.status !== 'PENDING' }"
  >
    <div
      class="card-header d-flex justify-content-between align-items-center py-2"
    >
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span class="badge bg-dark">{{ Math.round(pair.score * 100) }}%</span>
        <span
          v-for="reason in pair.match_reasons"
          :key="reason"
          class="badge bg-light text-dark border"
        >
          {{
            t(`ecolabels.duplicates.reasons.${reasonKey(reason)}`, {
              detail: reasonDetail(reason),
            })
          }}
        </span>
        <span v-if="pair.status !== 'PENDING'" class="badge bg-success">
          {{ t(`ecolabels.duplicates.statuses.${pair.status.toLowerCase()}`) }}
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <span
          v-if="pair.ai_assessment"
          class="badge"
          :class="aiBadgeClass"
          :title="pair.ai_assessment.rationale"
        >
          <font-awesome-icon icon="robot" class="me-1" />
          {{ t(`ecolabels.duplicates.ai.${recommendationKey}`) }}
          ({{ Math.round(pair.ai_assessment.confidence * 100) }}%)
        </span>
        <button
          v-else
          class="btn btn-outline-secondary btn-sm"
          :disabled="assessing"
          @click="assess"
        >
          <span
            v-if="assessing"
            class="spinner-border spinner-border-sm me-1"
          />
          <font-awesome-icon v-else icon="robot" class="me-1" />
          {{ t("ecolabels.duplicates.ask_ai") }}
        </button>
      </div>
    </div>

    <div class="card-body py-2">
      <div v-if="pair.ai_assessment" class="small text-muted fst-italic mb-2">
        {{ pair.ai_assessment.rationale }}
      </div>
      <div class="table-responsive">
        <table class="table table-sm mb-0 pair-table">
          <thead>
            <tr class="small text-muted">
              <th style="width: 12rem"></th>
              <th>
                {{ t("ecolabels.duplicates.device_a") }}
                <span
                  v-if="sideLabel(pair.device_a)"
                  class="badge ms-1"
                  :class="sideBadgeClass(pair.device_a)"
                >
                  {{ sideLabel(pair.device_a) }}
                </span>
              </th>
              <th>
                {{ t("ecolabels.duplicates.device_b") }}
                <span
                  v-if="sideLabel(pair.device_b)"
                  class="badge ms-1"
                  :class="sideBadgeClass(pair.device_b)"
                >
                  {{ sideLabel(pair.device_b) }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in comparisonRows"
              :key="row.label"
              :class="{ 'table-warning': row.differs }"
            >
              <td class="text-muted small">{{ row.label }}</td>
              <td>{{ row.a ?? "—" }}</td>
              <td>{{ row.b ?? "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="pair.status === 'PENDING'"
      class="card-footer d-flex justify-content-end align-items-center gap-2 py-2"
    >
      <template v-if="confirmingMergeInto === null">
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="resolving"
          @click="resolve('not_duplicate')"
        >
          {{ t("ecolabels.duplicates.not_duplicate") }}
        </button>
        <button
          class="btn btn-primary btn-sm"
          :disabled="resolving"
          @click="confirmingMergeInto = pair.device_a.id"
        >
          {{ t("ecolabels.duplicates.keep_a") }}
        </button>
        <button
          class="btn btn-primary btn-sm"
          :disabled="resolving"
          @click="confirmingMergeInto = pair.device_b.id"
        >
          {{ t("ecolabels.duplicates.keep_b") }}
        </button>
      </template>
      <template v-else>
        <span class="small text-muted me-2">
          {{
            t("ecolabels.duplicates.merge_confirm", { name: mergeTargetName })
          }}
        </span>
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="resolving"
          @click="confirmingMergeInto = null"
        >
          {{ t("common.cancel") }}
        </button>
        <button
          class="btn btn-danger btn-sm"
          :disabled="resolving"
          @click="resolve('merge', confirmingMergeInto)"
        >
          <span
            v-if="resolving"
            class="spinner-border spinner-border-sm me-1"
          />
          {{ t("ecolabels.duplicates.confirm_merge") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type { DuplicatePair, EcolabelDevice } from "../api/ecolabelsTypes";

const props = defineProps<{
  pair: DuplicatePair;
  compact?: boolean;
  /** When the pair is shown for a specific submission (approval flow), the id of that
   * device — its side is labeled "this submission", the other "existing device". */
  subjectDeviceId?: number;
}>();

// Which side is which: with a subject (approval flow) label submission vs existing;
// standalone (duplicates tab) orient by creation time instead.
const sideLabel = (device: EcolabelDevice): string | null => {
  if (props.subjectDeviceId != null) {
    return device.id === props.subjectDeviceId
      ? t("ecolabels.duplicates.this_submission")
      : t("ecolabels.duplicates.existing_device");
  }
  const other =
    device.id === props.pair.device_a.id
      ? props.pair.device_b
      : props.pair.device_a;
  if (device.created_at === other.created_at) return null;
  return device.created_at > other.created_at
    ? t("ecolabels.duplicates.newer_entry")
    : t("ecolabels.duplicates.older_entry");
};

const sideBadgeClass = (device: EcolabelDevice): string =>
  props.subjectDeviceId != null && device.id === props.subjectDeviceId
    ? "bg-primary"
    : "bg-secondary";

const emit = defineEmits<{
  resolved: [pair: DuplicatePair];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const assessing = ref(false);
const resolving = ref(false);
// Two-step merge confirmation, inline instead of window.confirm (which blocks the page).
const confirmingMergeInto = ref<number | null>(null);
const localAssessment = ref(props.pair.ai_assessment);
watch(
  () => props.pair.ai_assessment,
  (value) => {
    if (value) localAssessment.value = value;
  }
);

const pair = computed(() => ({
  ...props.pair,
  ai_assessment: localAssessment.value ?? props.pair.ai_assessment,
}));

const KNOWN_RECOMMENDATIONS = new Set(["duplicate", "not_duplicate", "unsure"]);
const recommendationKey = computed(() => {
  const value = pair.value.ai_assessment?.recommendation;
  return value && KNOWN_RECOMMENDATIONS.has(value) ? value : "unsure";
});

const mergeTargetName = computed(() =>
  confirmingMergeInto.value === props.pair.device_a.id
    ? props.pair.device_a.name
    : props.pair.device_b.name
);

interface ComparisonRow {
  label: string;
  a: string | null;
  b: string | null;
  differs: boolean;
}

const comparisonRows = computed<ComparisonRow[]>(() => {
  const fields: { label: string; get: (d: EcolabelDevice) => string | null }[] =
    [
      { label: t("ecolabels.table.name"), get: (d) => d.name },
      {
        label: t("ecolabels.table.plant_ecolabel_id"),
        get: (d) => d.plant_ecolabel_id,
      },
      {
        label: t("ecolabels.table.registration_authority"),
        get: (d) => d.registration_authority,
      },
      { label: t("ecolabels.table.country"), get: (d) => d.country },
      {
        label: t("ecolabels.table.renewable_type"),
        get: (d) => d.renewable_type,
      },
      {
        label: t("ecolabels.table.capacity"),
        get: (d) =>
          d.capacity !== null ? `${d.capacity} ${d.capacity_units}` : null,
      },
      {
        label: t("ecolabels.duplicates.coordinates"),
        get: (d) =>
          d.latitude !== null && d.longitude !== null
            ? `${d.latitude}, ${d.longitude}`
            : null,
      },
      { label: t("ecolabels.table.cod"), get: (d) => d.cod },
      { label: t("ecolabels.table.status"), get: (d) => d.status },
      { label: t("ecolabels.form.address"), get: (d) => d.address },
    ];
  const rows = fields.map(({ label, get }) => {
    const a = get(props.pair.device_a);
    const b = get(props.pair.device_b);
    return { label, a, b, differs: (a ?? "") !== (b ?? "") };
  });
  // In compact mode (inside the approval drawer) only show the informative rows.
  return props.compact
    ? rows.filter((row) => row.a !== null || row.b !== null)
    : rows;
});

const aiBadgeClass = computed(() => {
  switch (pair.value.ai_assessment?.recommendation) {
    case "duplicate":
      return "bg-warning text-dark";
    case "not_duplicate":
      return "bg-success";
    default:
      return "bg-secondary";
  }
});

async function assess() {
  assessing.value = true;
  try {
    const response = await ecolabelsApi.assessDuplicatePair(props.pair.id);
    localAssessment.value = response.data.assessment;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    assessing.value = false;
  }
}

async function resolve(
  resolution: "not_duplicate" | "merge",
  canonicalDeviceId?: number | null
) {
  if (resolution === "merge" && !canonicalDeviceId) return;
  resolving.value = true;
  try {
    const response = await ecolabelsApi.resolveDuplicatePair(
      props.pair.id,
      resolution,
      canonicalDeviceId ?? undefined
    );
    globalSuccessHandler(t("ecolabels.duplicates.resolved_toast"), "");
    confirmingMergeInto.value = null;
    emit("resolved", response.data);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    resolving.value = false;
  }
}

function reasonKey(reason: string): string {
  return reason.startsWith("name_fuzzy_") ? "name_fuzzy" : reason;
}

function reasonDetail(reason: string): string {
  return reason.startsWith("name_fuzzy_")
    ? `${Math.round(Number(reason.replace("name_fuzzy_", "")) * 100)}%`
    : "";
}
</script>

<style scoped lang="scss">
.pair-table td {
  vertical-align: top;
}
</style>
